/**
 * Procedural blood-splat geometry (spec 2026-08-23 §5.3).
 *
 * Owner decision D8 left image_assets.md unamended, so there are no blood decal PNGs and shape
 * has to come from somewhere -- here. Hard-edged straight segments only, no curves and no
 * gradient, which is what keeps a splat reading as the same hard-cel language as the rest of
 * the game (D9) rather than as a photoreal smear pasted onto a flat sprite.
 *
 * Pure and deterministic: the same seed always yields the same path, so a stain the server
 * rolled once looks identical on every surface and every session.
 */

/** The three flat red steps, deepest first. Matches shatter.js's own gore gib palette so a
 *  splat on the pet and the burst it came from read as the same substance -- these must stay
 *  the same three values as shatter.js's gore.gib.colors (in reverse order -- gib.colors is
 *  brightest-first, this is deepest-first), not routed through a CSS token, because a token
 *  indirection would let the two drift silently. */
export const STAIN_TONES = ['#3d060e', '#6d0f1c', '#a5182a'] as const; // raw-ok: same palette as gore.gib.colors in web/src/fx/shatter.js, reverse order -- must not drift, see comment above

/** Small deterministic PRNG (mulberry32) -- self-contained so this module has no dependency. */
function rng(seed: number): () => number {
  let a = (seed ^ 0x9e3779b9) >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const clamp = (v: number) => Math.max(0, Math.min(100, Math.round(v * 100) / 100));

/** One splat part: its centre and its outline, in the `0 0 100 100` viewBox (star-shaped about the centre). */
export type StainBlob = { cx: number; cy: number; pts: [number, number][] };

/** One irregular blob: a closed polygon whose radius wobbles per vertex. */
function blob(next: () => number, cx: number, cy: number, r: number, points: number): StainBlob {
  const pts: [number, number][] = [];
  for (let i = 0; i < points; i++) {
    const angle = (i / points) * Math.PI * 2;
    const wobble = r * (0.55 + next() * 0.75);
    pts.push([clamp(cx + Math.cos(angle) * wobble), clamp(cy + Math.sin(angle) * wobble)]);
  }
  return { cx: clamp(cx), cy: clamp(cy), pts };
}

/**
 * A central splat plus 3-6 satellite droplets, in a `0 0 100 100` viewBox — as outlines, so the 3D
 * stain (pet3d/stains3d.js) draws the same splat the 2D overlay does.
 * Every coordinate is clamped into the box so the path never overflows its own frame.
 */
export function stainBlobs(seed: number): StainBlob[] {
  const next = rng(Math.abs(Math.floor(seed)) % 0xffffffff);
  const blobs = [blob(next, 50, 50, 26, 9 + Math.floor(next() * 4))];
  const satellites = 3 + Math.floor(next() * 4);
  for (let i = 0; i < satellites; i++) {
    const angle = next() * Math.PI * 2;
    const dist = 26 + next() * 20;
    blobs.push(blob(
      next,
      50 + Math.cos(angle) * dist,
      50 + Math.sin(angle) * dist,
      3 + next() * 6,
      6,
    ));
  }
  return blobs;
}

/** The same splat as an SVG path. */
export function stainPath(seed: number): string {
  return stainBlobs(seed).map((b) => `M${b.pts.map(([x, y]) => `${x} ${y}`).join(' L')} Z`).join(' ');
}
